import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Package, FileCheck, LayoutGrid, Clock, Shield } from 'lucide-react';

const AIProductPhotographyAmazon = () => {
  const faqs = [
    {
      question: "Does the AI create Amazon-compliant product images?",
      answer: "Yes! Our AI generates images that meet Amazon's strict requirements: pure white backgrounds (RGB 255,255,255), product filling 85%+ of frame, high resolution (2000x2000px minimum), and no watermarks or logos."
    },
    {
      question: "Can I create A+ Content and infographic images?",
      answer: "Absolutely! Our platform generates lifestyle images, comparison charts, feature callouts, and infographic-style images perfect for Amazon A+ Content and Enhanced Brand Content."
    },
    {
      question: "What about different product angles and variations?",
      answer: "Generate multiple angles, close-ups, and variations of your products. Create consistent image sets for entire product catalogs with matching style and lighting."
    },
    {
      question: "Are these images safe to use on Amazon?",
      answer: "Yes, all images come with full commercial rights. They're AI-generated, so there are no model releases or copyright concerns. Perfect for Amazon FBA, FBM, and wholesale sellers."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Pure White Backgrounds",
      description: "Generate main images with Amazon-compliant pure white (255,255,255) backgrounds automatically"
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Product-First Framing",
      description: "AI ensures products fill 85%+ of the image frame per Amazon's requirements"
    },
    {
      icon: <LayoutGrid className="w-6 h-6" />,
      title: "A+ Content Ready",
      description: "Create lifestyle images, infographics, and comparison modules for Enhanced Brand Content"
    },
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "2000x2000px Resolution",
      description: "High-resolution images that meet Amazon's zoom functionality requirements"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Catalog Generation",
      description: "Generate entire product catalogs with consistent styling in minutes, not days"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Launch Products Faster",
      description: "Skip the photography wait times. Generate product images and list immediately"
    }
  ];

  const useCases = [
    "Amazon FBA private label sellers",
    "Wholesale and arbitrage resellers",
    "Brand owners creating A+ Content",
    "Agencies managing multiple seller accounts",
    "Dropshippers needing quick product images",
    "New product launches requiring fast turnaround",
    "Seasonal inventory updates",
    "International sellers expanding catalogs"
  ];

  const requirements = [
    "Pure white background (RGB 255,255,255)",
    "Product fills 85% or more of image",
    "2000x2000px minimum for zoom",
    "JPEG, TIFF, PNG, or GIF format",
    "No watermarks, logos, or text",
    "Product must be in-focus and well-lit"
  ];

  return (
    <>
      <SEO
        title="AI Product Photography for Amazon | FBA Seller Image Generator"
        description="Create Amazon-compliant product photos with AI. White background main images, A+ content, infographics. Pass Amazon image requirements every time. Trusted by 5,000+ FBA sellers."
        keywords="amazon product photography ai, amazon fba photos, amazon listing images, amazon product images ai, fba seller tools, amazon image requirements, amazon a+ content images, amazon fba product photography"
        url="/ai-product-photography-amazon"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Product Photography for Amazon', url: '/ai-product-photography-amazon' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-amber-500/10 text-amber-600 border-amber-500/20">
                Amazon FBA Seller Approved
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Product Photography
                <span className="block text-amber-500">for Amazon Sellers</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate Amazon-compliant product images instantly. White backgrounds, A+ Content, 
                infographics. Pass Amazon's image requirements every time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-black">
                  <Link to="/try-generator">
                    Create Amazon Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Amazon Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Amazon Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Commercial Rights</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <span>5,000+ FBA Sellers</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Amazon Requirements Checklist */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Amazon Image Requirements? ✓ Covered
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Our AI automatically generates images that meet all Amazon standards
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Built for Amazon FBA Success
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-amber-500/10 hover:border-amber-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Amazon Sellers
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Launch Products Faster on Amazon
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 5,000+ Amazon sellers using AI to create compliant product images. 
                Skip the photography wait and list products immediately.
              </p>
              <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-black">
                <Link to="/try-generator">
                  Generate Amazon Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIProductPhotographyAmazon;
